import React, { useId } from "react";
import { ChevronDown } from "lucide-react";

/**
 * One part of the Agents tab: a heading, a short line under it, and the body.
 *
 * A part that can close shows only its heading while closed, so the tab can
 * hold what a person needs now open and tuck the rest away.
 */

interface AgentsSectionProps {
  title: string;
  /** Pass it when something else points at the heading, e.g. a textarea. */
  titleId?: string;
  description?: React.ReactNode;
  descriptionId?: string;
  /** Given together with onToggle, the heading opens and closes the part. */
  expanded?: boolean;
  onToggle?: () => void;
  children: React.ReactNode;
}

const AgentsSection: React.FC<AgentsSectionProps> = ({
  title,
  titleId,
  description,
  descriptionId,
  expanded = true,
  onToggle,
  children,
}) => {
  const fallbackTitleId = useId();
  const bodyId = useId();
  const headingId = titleId ?? fallbackTitleId;
  const collapsible = onToggle !== undefined;
  const open = !collapsible || expanded;

  return (
    <section aria-labelledby={headingId} className="py-6 first:pt-0 last:pb-0">
      {/* Like a list heading on the board: the arrow sits before the title,
          points right while closed and down while open, and the title turns
          the accent color on hover — no box. The row runs edge to edge of the
          scrolling pane, so a box or ring around it would be cut off there. */}
      <div className="group relative flex items-start gap-2.5">
        {collapsible && (
          <ChevronDown
            size={16}
            aria-hidden="true"
            className={`mt-[3px] flex-shrink-0 text-warm-text-tertiary transition-transform duration-200 ${
              open ? "" : "-rotate-90"
            }`}
          />
        )}
        <div className="flex-1 min-w-0">
          <h4
            id={headingId}
            className={`font-heading text-[15px] font-semibold text-warm-text-primary ${
              collapsible
                ? "group-hover:text-warm-blue group-has-[:focus-visible]:text-warm-blue transition-colors"
                : ""
            }`}
          >
            {collapsible ? (
              // The ::after layer stretches the button over the whole row, so
              // the line under the heading opens the part too.
              <button
                type="button"
                aria-expanded={open}
                aria-controls={bodyId}
                onClick={onToggle}
                className="text-left focus:outline-none focus-visible:underline focus-visible:decoration-2 focus-visible:underline-offset-4 after:absolute after:inset-0 after:content-['']"
              >
                {title}
              </button>
            ) : (
              title
            )}
          </h4>
          {description && (
            <p id={descriptionId} className="mt-0.5 text-xs text-warm-text-tertiary">
              {description}
            </p>
          )}
        </div>
      </div>

      {/* Kept in the page while closed, so the button's aria-controls always
          points at something. hidden takes it out of view and out of the
          accessibility tree. */}
      <div id={bodyId} hidden={!open} className="mt-4">
        {children}
      </div>
    </section>
  );
};

export default AgentsSection;
